<template>
  <div class="rb-screening-q" :data-question-id="question.question_id">
    <div v-if="choiceContractIssue" class="rb-ob-field-block" data-screening-contract-error>
      <p v-if="promptText" class="rb-ob-q">{{ promptText }}</p>
      <p class="rb-ob-err is-visible" role="alert">{{ $t('intake.screeningUi.emptyChoices') }}</p>
      <div class="rb-ob-actions">
        <button class="rb-ob-submit" type="button" :disabled="busy" @click="$emit('retry')">{{ $t('intake.screeningUi.retryQuestion') }}</button>
        <button v-if="canGoBack" class="rb-ob-submit rb-ob-submit-secondary" type="button" :disabled="busy" @click="$emit('back')">{{ $t('intake.screeningUi.backQuestion') }}</button>
      </div>
    </div>
    <!-- Specialized layouts from server `layout` (fallback: question_id / type) -->
    <ScreeningParticipantSelect
        v-else-if="layout === 'participant_select'"
        ref="specialized"
        :question="question"
        @change="$emit('change', $event)"
    />

    <ScreeningAgeInputs
        v-else-if="layout === 'age_inputs'"
        ref="specialized"
        :question="question"
        :s1-intent="s1Intent"
        @change="$emit('change', $event)"
    />

    <ScreeningTopicsSelect
        v-else-if="layout === 'grouped_multi_select'"
        ref="specialized"
        :question="question"
        :service-track-id="serviceTrackId"
        @change="$emit('change', $event)"
    />

    <template v-else>
      <p v-if="promptText" class="rb-ob-q">{{ promptText }}</p>
      <p v-if="question.required === false" class="rb-ob-q-hint">{{ $t('intake.screeningUi.optionalPreference') }}</p>
      <p v-if="questionId === 'O5'" class="rb-ob-q-hint">{{ $t('intake.screeningUi.flexibilityHelp') }}</p>
      <p v-if="questionId === 'C2'" class="rb-ob-q-hint">{{ $t('intake.screeningUi.pastCareHelp') }}</p>
      <p v-if="questionId === 'C4'" class="rb-ob-q-hint">{{ $t('intake.screeningUi.currentCareHelp') }}</p>
      <p v-if="question.recall === 'PAST_2_WEEKS'" class="rb-ob-q-hint">{{ $t('intake.screeningUi.recallTwoWeeks') }}</p>

      <!-- SINGLE_SELECT / YES_NO -->
      <div
          v-if="isSingle"
          class="rb-ob-choice-grid"
          :class="{ 'is-two-col': options.length > 2 }"
      >
        <div
            v-for="opt in options"
            :key="opt.id"
            class="rb-ob-choice"
            :class="{ 'is-selected': singleValue === opt.id }"
        >
          <label class="rb-ob-choice-select">
            <input
                v-model="singleValue"
                type="radio"
                :name="'sq-' + questionId"
                :value="opt.id"
                @change="emitChange"
            >
            <span class="rb-ob-choice-body">
              <span class="rb-ob-choice-title">{{ opt.label || opt.id }}</span>
            </span>
          </label>
        </div>
      </div>

      <!-- MULTI_SELECT -->
      <div
          v-else-if="type === 'MULTI_SELECT'"
          class="rb-ob-choice-grid is-two-col"
      >
        <div
            v-for="opt in options"
            :key="opt.id"
            class="rb-ob-choice"
            :class="{ 'is-selected': multiValues.indexOf(opt.id) !== -1 }"
        >
          <label class="rb-ob-choice-select">
            <input
                type="checkbox"
                :name="'sq-' + questionId"
                :value="opt.id"
                :checked="multiValues.indexOf(opt.id) !== -1"
                @change="toggleMulti(opt)"
            >
            <span class="rb-ob-choice-body">
              <span class="rb-ob-choice-title">{{ opt.label || opt.id }}</span>
            </span>
          </label>
        </div>
      </div>

      <!-- INTEGER_SCALE -->
      <div v-else-if="layout === 'integer_scale' || type === 'INTEGER_SCALE'" class="rb-ob-field-block">
        <div class="rb-ob-choice-grid is-dense">
          <div
              v-for="n in scaleValues"
              :key="'sc-' + n"
              class="rb-ob-choice"
              :class="{ 'is-selected': singleValue === String(n) }"
          >
            <label class="rb-ob-choice-select">
              <input
                  v-model="singleValue"
                  type="radio"
                  :name="'sq-' + questionId"
                  :value="String(n)"
                  @change="emitChange"
              >
              <span class="rb-ob-choice-body">
                <span class="rb-ob-choice-title">{{ n }}</span>
              </span>
            </label>
          </div>
        </div>
      </div>

      <!-- CONDITIONAL_AGE_INPUTS (non-specialized fallback) -->
      <div v-else-if="type === 'CONDITIONAL_AGE_INPUTS'" class="rb-ob-field-block">
        <template v-if="ageMode === 'MULTI_SELECT_POPULATION_BANDS'">
          <div class="rb-ob-choice-grid">
            <div
                v-for="opt in ageBandOptions"
                :key="opt.id"
                class="rb-ob-choice"
                :class="{ 'is-selected': multiValues.indexOf(opt.id) !== -1 }"
            >
              <label class="rb-ob-choice-select">
                <input
                    type="checkbox"
                    :value="opt.id"
                    :checked="multiValues.indexOf(opt.id) !== -1"
                    @change="toggleAgeBand(opt)"
                >
                <span class="rb-ob-choice-body">
                  <span class="rb-ob-choice-title">{{ opt.label || opt.id }}</span>
                </span>
              </label>
            </div>
          </div>
        </template>
        <template v-else>
          <div
              v-for="field in ageFields"
              :key="field.role"
              class="rb-ob-field-block"
              style="margin-bottom: 12px"
          >
            <label :for="'age-' + field.role">{{ field.label || agePrompt || 'سن' }}</label>
            <input
                :id="'age-' + field.role"
                v-model="ageByRole[field.role]"
                class="rb-ob-control"
                type="text"
                inputmode="numeric"
                autocomplete="off"
                @input="emitChange"
            >
          </div>
        </template>
      </div>

      <!-- WEEKLY_AVAILABILITY -->
      <div v-else-if="layout === 'availability_grid' || type === 'WEEKLY_AVAILABILITY'" class="rb-ob-field-block">
        <div class="rb-ob-week-grid">
          <div v-for="day in dayKeys" :key="day" class="rb-ob-day-row">
            <div class="rb-ob-day">{{ dayLabel(day) }}</div>
            <div class="rb-ob-slots">
              <label
                  v-for="slot in slotKeys"
                  :key="day + '/' + slot"
                  class="rb-ob-chip"
                  :class="{ 'is-selected': multiValues.indexOf(day + '/' + slot) !== -1 }"
              >
                <input
                    type="checkbox"
                    :value="day + '/' + slot"
                    :aria-label="dayLabel(day) + ' ' + slotLabel(slot)"
                    :checked="multiValues.indexOf(day + '/' + slot) !== -1"
                    @change="toggleSlot(day, slot)"
                >
                <span>{{ slotLabel(slot) }}</span>
              </label>
            </div>
          </div>
        </div>
      </div>

      <p v-else class="rb-ob-err is-visible">نوع سوال پشتیبانی نمی‌شود: {{ type || layout }}</p>
    </template>
  </div>
</template>

<script>
import {DAY_KEYS, SLOT_KEYS} from '@/intake/intakeState'
import ScreeningParticipantSelect from '@/intake/ScreeningParticipantSelect.vue'
import ScreeningAgeInputs from '@/intake/ScreeningAgeInputs.vue'
import ScreeningTopicsSelect from '@/intake/ScreeningTopicsSelect.vue'

export default {
  name: 'ScreeningQuestionRenderer',
  components: {
    ScreeningParticipantSelect,
    ScreeningAgeInputs,
    ScreeningTopicsSelect,
  },
  props: {
    question: {
      type: Object,
      required: true,
    },
    s1Intent: {
      type: String,
      default: null,
    },
    serviceTrackId: {
      type: String,
      default: null,
    },
    busy: {
      type: Boolean,
      default: false,
    },
    canGoBack: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      singleValue: '',
      multiValues: [],
      ageByRole: {},
      dayKeys: DAY_KEYS,
      slotKeys: SLOT_KEYS,
    }
  },
  computed: {
    questionId() {
      return (this.question && this.question.question_id) || ''
    },
    type() {
      return (this.question && this.question.type) || ''
    },
    layout() {
      const explicit = this.question && this.question.layout
      if (explicit) {
        return String(explicit)
      }
      if (this.questionId === 'S1') {
        return 'participant_select'
      }
      if (this.questionId === 'S2' || this.type === 'CONDITIONAL_AGE_INPUTS') {
        return 'age_inputs'
      }
      if (this.questionId === 'P1' || (this.question && this.question.display === 'GROUPED_MULTI_SELECT')) {
        return 'grouped_multi_select'
      }
      if (this.type === 'WEEKLY_AVAILABILITY') {
        return 'availability_grid'
      }
      if (this.type === 'INTEGER_SCALE') {
        return 'integer_scale'
      }
      return 'choice_grid'
    },
    options() {
      const raw = (this.question && Array.isArray(this.question.options))
        ? this.question.options.filter((o) => o && o.id)
        : []
      if (raw.length) {
        return raw
      }
      if (this.type === 'YES_NO') {
        return [
          {id: 'YES', label: 'بله'},
          {id: 'NO', label: 'خیر'},
        ]
      }
      return []
    },
    isChoiceQuestion() {
      return this.isSingle || this.type === 'MULTI_SELECT' || this.layout === 'grouped_multi_select'
    },
    choiceContractIssue() {
      if (!this.isChoiceQuestion) return null
      if (this.layout === 'grouped_multi_select') {
        const groups = Array.isArray(this.question.option_groups) ? this.question.option_groups : []
        const groupedOptions = groups.some(group => group && Array.isArray(group.options)
          && group.options.some(option => option && option.id))
        const unsure = this.question.unsure_option
        if (groupedOptions || (unsure && unsure.id)) return null
      }
      return this.options.length ? null : 'QUESTION_OPTIONS_UNAVAILABLE'
    },
    isSingle() {
      return this.type === 'SINGLE_SELECT' || this.type === 'YES_NO'
    },
    promptText() {
      if (this.type === 'CONDITIONAL_AGE_INPUTS') {
        return this.agePrompt || (this.question && this.question.text) || ''
      }
      return (this.question && this.question.text) || ''
    },
    intentKey() {
      return this.s1Intent
        || (this.question && this.question.s1_intent)
        || null
    },
    agePresentation() {
      const map = (this.question && this.question.presentation_by_intent) || {}
      if (this.intentKey && map[this.intentKey]) {
        return map[this.intentKey]
      }
      return null
    },
    ageMode() {
      return (this.agePresentation && this.agePresentation.mode) || 'SINGLE_AGE'
    },
    agePrompt() {
      return (this.agePresentation && this.agePresentation.prompt) || ''
    },
    ageFields() {
      return (this.agePresentation && Array.isArray(this.agePresentation.fields))
        ? this.agePresentation.fields
        : []
    },
    ageBandOptions() {
      if (this.agePresentation && Array.isArray(this.agePresentation.options)) {
        return this.agePresentation.options
      }
      return this.options
    },
    scaleValues() {
      const v = (this.question && this.question.validation) || {}
      const min = Number(v.min != null ? v.min : 0)
      const max = Number(v.max != null ? v.max : 10)
      const out = []
      for (let i = min; i <= max; i += 1) {
        out.push(i)
      }
      return out
    },
  },
  watch: {
    question: {
      immediate: true,
      handler() {
        this.resetLocal()
      },
    },
    s1Intent() {
      if (this.layout === 'age_inputs' || this.type === 'CONDITIONAL_AGE_INPUTS') {
        this.resetLocal()
      }
    },
  },
  methods: {
    resetLocal() {
      const prefill = this.question.answer_prefill || {}
      const rawIds = Array.isArray(prefill.answer_ids) ? prefill.answer_ids : []
      const ids = this.isChoiceQuestion
        ? rawIds.filter(id => this.options.some(option => option.id === id))
        : rawIds
      this.singleValue = ids[0] || ''
      this.multiValues = ids.slice()
      const ages = {}
      this.ageFields.forEach((f) => {
        const saved = (prefill.ages || []).find((item) => item.role === f.role)
        ages[f.role] = saved ? String(saved.age) : ''
      })
      this.ageByRole = ages
      this.$emit('contract-state', {
        question_id: this.questionId,
        instance_key: this.question.instance_key || null,
        issue: this.choiceContractIssue,
      })
      this.emitChange()
    },
    dayLabel(day) {
      try {
        return this.$t('intake.dayShort.' + day)
      } catch (e) {
        return day
      }
    },
    slotLabel(slot) {
      try {
        return this.$t('intake.slot.' + slot)
      } catch (e) {
        return slot
      }
    },
    toggleMulti(opt) {
      const id = opt.id
      const exclusiveIds = ['NONE', 'UNSURE', 'NO', 'PREFER_NOT', 'BASELINE']
      if (opt.exclusive || exclusiveIds.indexOf(id) !== -1) {
        this.multiValues = this.multiValues.indexOf(id) !== -1 ? [] : [id]
        this.emitChange()
        return
      }
      let next = this.multiValues.filter((x) => exclusiveIds.indexOf(x) === -1)
      const idx = next.indexOf(id)
      if (idx === -1) {
        next.push(id)
      } else {
        next.splice(idx, 1)
      }
      this.multiValues = next
      this.emitChange()
    },
    toggleAgeBand(opt) {
      const id = opt.id
      if (opt.exclusive || id === 'ADULTS_ONLY') {
        this.multiValues = this.multiValues.indexOf(id) !== -1 ? [] : [id]
        this.emitChange()
        return
      }
      let next = this.multiValues.filter((x) => x !== 'ADULTS_ONLY')
      const idx = next.indexOf(id)
      if (idx === -1) {
        next.push(id)
      } else {
        next.splice(idx, 1)
      }
      this.multiValues = next
      this.emitChange()
    },
    toggleSlot(day, slot) {
      const key = day + '/' + slot
      const idx = this.multiValues.indexOf(key)
      if (idx === -1) {
        this.multiValues = this.multiValues.concat([key])
      } else {
        const next = this.multiValues.slice()
        next.splice(idx, 1)
        this.multiValues = next
      }
      this.emitChange()
    },
    buildPayload() {
      if (this.choiceContractIssue) return {valid: false, answer_ids: []}
      const required = !(this.question && this.question.required === false)
      if (this.isSingle || this.type === 'INTEGER_SCALE' || this.layout === 'integer_scale') {
        const selected = this.singleValue && (!this.isSingle || this.options.some(option => option.id === this.singleValue))
        const ids = selected ? [this.singleValue] : []
        return {
          valid: !required || ids.length > 0,
          answer_ids: ids,
        }
      }
      if (this.type === 'MULTI_SELECT' || this.type === 'WEEKLY_AVAILABILITY' || this.layout === 'availability_grid') {
        const ids = this.type === 'MULTI_SELECT'
          ? this.multiValues.filter(id => this.options.some(option => option.id === id))
          : this.multiValues.slice()
        return {
          valid: !required || ids.length > 0,
          answer_ids: ids,
        }
      }
      if (this.type === 'CONDITIONAL_AGE_INPUTS' || this.layout === 'age_inputs') {
        if (this.ageMode === 'MULTI_SELECT_POPULATION_BANDS') {
          return {
            valid: this.multiValues.length > 0,
            answer_ids: this.multiValues.slice(),
            ages: [],
          }
        }
        const ages = []
        let valid = true
        this.ageFields.forEach((field) => {
          const raw = this.ageByRole[field.role]
          const age = parseInt(String(raw || '').replace(/[^\d]/g, ''), 10)
          if (!Number.isFinite(age)) {
            valid = false
            return
          }
          const min = field.min != null ? Number(field.min) : 0
          const max = field.max != null ? Number(field.max) : 120
          if (age < min || age > max) {
            valid = false
          }
          ages.push({role: field.role, age})
        })
        return {
          valid: valid && ages.length > 0,
          answer_ids: [],
          ages,
        }
      }
      return {valid: false, answer_ids: []}
    },
    emitChange() {
      this.$emit('change', this.buildPayload())
    },
    /** Parent calls before submit. */
    getAnswer() {
      if (this.choiceContractIssue) return {valid: false, answer_ids: []}
      if (this.$refs.specialized && this.$refs.specialized.getAnswer) {
        return this.$refs.specialized.getAnswer()
      }
      return this.buildPayload()
    },
  },
}
</script>
